import { create } from 'zustand';
import { portalApi, ConversationSummary } from '../pages/portal/portalApi';
import type { PendingTurn, PendingTurnStatus } from '../pages/portal/types';

const PAGE_SIZE = 15;
let conversationsFetchVersion = 0;

interface PortalState {
  activeConversationId: string | null;
  conversations: ConversationSummary[];
  pendingTurns: Record<string, PendingTurn>;
  sidebarOpen: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;

  fetchConversations: () => Promise<void>;
  fetchMoreConversations: () => Promise<void>;
  setActiveConversation: (id: string | null) => void;
  removeConversation: (id: string) => void;
  addPendingTurn: (turnId: string, entry: Omit<PendingTurn, 'status' | 'startedAt'>) => void;
  resolvePendingTurn: (turnId: string) => void;
  markPendingTurnFailed: (turnId: string, status: Exclude<PendingTurnStatus, 'sending'>) => void;
  dismissPendingTurn: (turnId: string) => void;
  newChat: () => void;
  reset: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

export const usePortalStore = create<PortalState>((set, get) => ({
  activeConversationId: null,
  conversations: [],
  pendingTurns: {},
  sidebarOpen: window.innerWidth > 768,
  loading: false,
  loadingMore: false,
  hasMore: false,

  fetchConversations: async () => {
    const version = ++conversationsFetchVersion;
    set({ loading: true });
    try {
      const result = await portalApi.listConversations(undefined, PAGE_SIZE);
      if (version !== conversationsFetchVersion) return;
      set({ conversations: result.conversations, hasMore: result.hasMore, loading: false });
    } catch (err) {
      if (version !== conversationsFetchVersion) return;
      console.warn('[portal] Failed to fetch conversations:', err);
      set({ loading: false });
    }
  },

  fetchMoreConversations: async () => {
    const { loadingMore, hasMore, conversations } = get();
    if (loadingMore || !hasMore || conversations.length === 0) return;

    set({ loadingMore: true });
    try {
      // Use the last conversation's updatedAt as cursor
      const lastConv = conversations[conversations.length - 1];
      const cursor = lastConv.updatedAt || lastConv.createdAt;
      const result = await portalApi.listConversations(cursor, PAGE_SIZE);
      set(s => ({
        conversations: [...s.conversations, ...result.conversations],
        hasMore: result.hasMore,
        loadingMore: false,
      }));
    } catch (err) {
      console.warn('[portal] Failed to fetch more conversations:', err);
      set({ loadingMore: false });
    }
  },

  setActiveConversation: (id) => {
    set({ activeConversationId: id });
  },

  removeConversation: (id) => {
    const { conversations, activeConversationId } = get();
    set({
      conversations: conversations.filter(c => c.id !== id),
      activeConversationId: activeConversationId === id ? null : activeConversationId,
    });
  },

  addPendingTurn: (turnId, entry) => {
    set(s => ({
      pendingTurns: {
        ...s.pendingTurns,
        [turnId]: { ...entry, status: 'sending', startedAt: new Date().toISOString() },
      },
    }));
  },

  resolvePendingTurn: (turnId) => {
    set(s => {
      const { [turnId]: _removed, ...rest } = s.pendingTurns;
      return { pendingTurns: rest };
    });
  },

  markPendingTurnFailed: (turnId, status) => {
    set(s => {
      const existing = s.pendingTurns[turnId];
      if (!existing) return s;
      return { pendingTurns: { ...s.pendingTurns, [turnId]: { ...existing, status } } };
    });
  },

  dismissPendingTurn: (turnId) => {
    set(s => {
      const { [turnId]: _removed, ...rest } = s.pendingTurns;
      return { pendingTurns: rest };
    });
  },

  newChat: () => {
    set({ activeConversationId: null });
  },

  reset: () => {
    set({
      activeConversationId: null,
      conversations: [],
      pendingTurns: {},
      loading: false,
      loadingMore: false,
      hasMore: false,
    });
  },

  toggleSidebar: () => {
    set(s => ({ sidebarOpen: !s.sidebarOpen }));
  },

  setSidebarOpen: (open) => {
    set({ sidebarOpen: open });
  },
}));
